if (!customElements.get('fo-coffee-story')) {
  customElements.define('fo-coffee-story', class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      this.scenes = [...this.querySelectorAll('[data-scene]')];
      if (!this.scenes.length) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.weights = this.scenes.map(s => Number(s.dataset.weight) || 100);
      this.total = this.weights.reduce((a, b) => a + b, 0);
      this.cuts = this.weights.map((_, i) => this.weights.slice(0, i).reduce((a, b) => a + b, 0));
      this.stage = this.querySelector('[data-stage]');
      this.character = this.querySelector('.fo-character');
      this.bubble = this.querySelector('.fo-bubble');
      this.counter = this.querySelector('[data-counter]');
      this.meter = this.querySelector('[data-meter]');
      this.active = -1;
      this.request = () => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; this.draw(); }); };
      this.resize = () => {
        this.style.setProperty('--fo-distance', `${this.total * (Number(this.dataset.pace) || 35) / 100}vh`);
        this.classList.toggle('fo-reduced', this.reduced.matches);
        if(this.reduced.matches)this.scenes.forEach(s=>{s.inert=false;s.removeAttribute('aria-hidden')});
        this.active=-1;
        this.request();
      };
      this.pointer = e => {
        if (this.reduced.matches || e.pointerType === 'touch' || !this.character) return;
        const r = this.character.getBoundingClientRect();
        const dx = e.clientX - r.left - r.width / 2, dy = e.clientY - r.top - r.height * .25;
        const d = Math.hypot(dx, dy) || 1;
        this.style.setProperty('--fo-eye-x', `${dx / d * Math.min(6, d / 35)}px`);
        this.style.setProperty('--fo-eye-y', `${dy / d * Math.min(5, d / 35)}px`);
      };
      this.editorSelect = e => {
        const i = this.scenes.findIndex(s => s.contains(e.target) || s === e.target);
        if (i >= 0) this.go(i, false);
      };
      this.prev = () => this.go(this.active - 1);
      this.next = () => this.go(this.active + 1);
      this.querySelector('[data-prev]')?.addEventListener('click', this.prev);
      this.querySelector('[data-next]')?.addEventListener('click', this.next);
      document.addEventListener('scroll', this.request, { passive: true, capture: true });
      window.addEventListener('resize', this.resize, { passive: true });
      this.addEventListener('pointermove', this.pointer, { passive: true });
      this.addEventListener('shopify:block:select', this.editorSelect);
      this.reduced.addEventListener('change', this.resize);
      this.cleanup = true;
      this.classList.add('fo-ready');
      this.resize();
    }
    go(i, smooth = true) {
      i = Math.max(0, Math.min(this.scenes.length - 1, i));
      if (this.reduced.matches) { this.scenes[i].scrollIntoView({ behavior: 'auto', block: 'start' }); return; }
      const top = this.getBoundingClientRect().top + scrollY;
      const distance = this.offsetHeight - this.stage.offsetHeight;
      const offset = parseFloat(getComputedStyle(this).getPropertyValue('--fo-top')) || 0;
      window.scrollTo({ top: top - offset + distance * (this.cuts[i] + this.weights[i] * .18) / this.total, behavior: smooth ? 'smooth' : 'auto' });
    }
    draw() {
      if (!this.isConnected || this.reduced.matches) return;
      const top = parseFloat(getComputedStyle(this).getPropertyValue('--fo-top')) || 0;
      const distance = Math.max(1, this.offsetHeight - this.stage.offsetHeight);
      const p = Math.max(0, Math.min(1, (top - this.getBoundingClientRect().top) / distance));
      const at = p * this.total;
      let i = this.scenes.length - 1;
      for (let j = 0; j < this.scenes.length; j++) { if (at < this.cuts[j] + this.weights[j]) { i = j; break; } }
      const local = Math.max(0, Math.min(1, (at - this.cuts[i]) / this.weights[i]));
      if (this.active !== i) {
        this.active = i;
        this.scenes.forEach((s, j) => { s.classList.toggle('fo-active', j === i); s.inert = j !== i; s.setAttribute('aria-hidden', String(j !== i)); });
        const s = this.scenes[i];
        this.dataset.mode = s.dataset.mode;
        this.character.hidden = s.dataset.character !== 'true';
        this.bubble.textContent = s.dataset.bubble || '';
        this.bubble.hidden = !s.dataset.bubble;
        this.counter.textContent = `${String(i + 1).padStart(2, '0')} / ${String(this.scenes.length).padStart(2, '0')}`;
        this.querySelector('[data-prev]').disabled = i === 0;
        this.querySelector('[data-next]').disabled = i === this.scenes.length - 1;
      }
      this.style.setProperty('--fo-progress', p);
      this.scenes[i].style.setProperty('--fo-local', local);
      const mode = this.scenes[i].dataset.mode;
      const turn = mode === 'travel' ? Math.sin(local * 45) * 8 : mode === 'joy' ? Math.sin(local * 15) * 7 : -5 + local * 6;
      const lift = mode === 'travel' ? Math.sin(local * 60) * 9 : mode === 'joy' ? -Math.abs(Math.sin(local * 15)) * 22 : 0;
      this.character.style.setProperty('--fo-turn', `${turn}deg`);
      this.character.style.setProperty('--fo-lift', `${lift}px`);
      this.character.style.setProperty('--fo-breath', mode === 'opening' ? 1 + Math.sin(local * Math.PI) * .055 : 1);
      this.style.setProperty('--fo-steam', mode === 'opening' ? local : 0);
      this.meter.setAttribute('aria-valuenow', String(Math.round(p * 100)));
    }
    disconnectedCallback() {
      if (!this.cleanup) return;
      document.removeEventListener('scroll', this.request, true);
      window.removeEventListener('resize', this.resize);
      this.removeEventListener('pointermove', this.pointer);
      this.removeEventListener('shopify:block:select', this.editorSelect);
      this.reduced.removeEventListener('change', this.resize);
      this.querySelector('[data-prev]')?.removeEventListener('click', this.prev);
      this.querySelector('[data-next]')?.removeEventListener('click', this.next);
      cancelAnimationFrame(this.raf);
      this.cleanup = false;
    }
  });
}
